import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/lib/context';
import BookingForm from '@/components/BookingForm';
import { GlobalNav } from '@/components/GlobalNav';
import { Card, CardContent } from '@/components/ui/card';
import { motion } from 'framer-motion';
import { 
  Calendar, Clock, Zap, Shield, CheckCircle, Users, Globe,
  Sparkles, Star, Heart
} from 'lucide-react';
import {
  useSmartDefaults,
  TimeGreeting,
  SocialProofBanner,
  MotivationalTip
} from '@/components/UserLove';

/**
 * Booking Landing Page - User Love Edition
 * Universal booking interface with emotional engagement
 */
const BookingPage = () => {
  const { t, language } = useLanguage();
  const { isReturningUser, getGreeting, preferences } = useSmartDefaults();
  const [showGreeting, setShowGreeting] = useState(true);

  // Hide greeting after a few seconds
  useEffect(() => {
    const timer = setTimeout(() => setShowGreeting(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  const features = [
    {
      icon: Calendar,
      title: t('booking.features.scheduling') || 'Easy Scheduling',
      description: t('booking.features.schedulingDesc') || 'Pick your preferred date and time'
    },
    {
      icon: Users,
      title: t('booking.features.matching') || 'AI Matching',
      description: t('booking.features.matchingDesc') || 'We find the best providers for you'
    },
    {
      icon: Zap,
      title: t('booking.features.quickResponse') || 'Quick Response',
      description: t('booking.features.quickResponseDesc') || 'PRO users get responses in 10-15 min'
    },
    {
      icon: Shield,
      title: t('booking.features.secure') || 'Secure & Private',
      description: t('booking.features.secureDesc') || 'Your data is protected'
    }
  ];

  // Hero messages based on language
  const heroMessages = {
    en: { 
      tagline: "Find help fast. No hassle.",
      subtitle: "Select your service, pick a time. AI matches you with the right providers instantly."
    },
    et: { 
      tagline: "Leia abi kiirelt. Vaevata.",
      subtitle: "Vali teenus, vali aeg. AI leiab sulle sobivad tegijad koheselt."
    },
    fi: { 
      tagline: "Löydä apua nopeasti. Vaivatta.",
      subtitle: "Valitse palvelu, valitse aika. AI löytää sinulle sopivat tarjoajat välittömästi."
    }
  };

  const hero = heroMessages[language] || heroMessages.en;

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50">
      {/* Global Navigation */}
      <GlobalNav />

      <main className="max-w-6xl mx-auto px-4 py-8 md:py-12">
        {/* Time-based greeting - shows briefly */}
        {showGreeting && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="text-center mb-6"
          >
            <TimeGreeting language={language} />
          </motion.div>
        )}

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Left: Hero Content */}
          <div className="space-y-8">
            {/* Main Hero */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-100 to-yellow-100 text-orange-700 rounded-full text-sm font-medium mb-6">
                <Sparkles className="h-4 w-4" />
                {isReturningUser 
                  ? ({ en: 'Welcome back!', et: 'Tere tulemast tagasi!', fi: 'Tervetuloa takaisin!' }[language] || 'Welcome back!')
                  : (t('booking.badge') || 'Universal Booking')
                }
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-4 leading-tight" data-testid="hero-title">
                {hero.tagline}
              </h1>
              
              <p className="text-lg md:text-xl text-slate-600 max-w-lg" data-testid="hero-subtitle">
                {hero.subtitle}
              </p>
            </motion.div>

            {/* Trust Indicators */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="flex flex-wrap gap-4 text-sm text-slate-600"
            >
              <span className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-full shadow-sm">
                <CheckCircle className="h-4 w-4 text-green-500" />
                {t('booking.trust.free') || 'Free to use'}
              </span>
              <span className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-full shadow-sm">
                <Shield className="h-4 w-4 text-blue-500" />
                {t('booking.trust.noLogin') || 'No login required'}
              </span>
              <span className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-full shadow-sm">
                <Globe className="h-4 w-4 text-purple-500" />
                {t('booking.trust.global') || '27+ countries'}
              </span>
            </motion.div>

            {/* Features Grid */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="grid sm:grid-cols-2 gap-4"
            >
              {features.map((feature, idx) => (
                <motion.div 
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + idx * 0.1 }}
                  className="flex items-start gap-3 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow border border-slate-100"
                >
                  <div className="p-2 bg-gradient-to-br from-orange-100 to-yellow-100 rounded-lg">
                    <feature.icon className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-900">{feature.title}</h3>
                    <p className="text-sm text-slate-500">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Motivational Tip */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              <MotivationalTip language={language} context="booking" />
            </motion.div>

            {/* Social Proof - Desktop only */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              className="hidden lg:block pt-4"
            >
              <SocialProofBanner 
                language={language} 
                stats={{ providers: 500, requests: 10000, countries: 27 }}
              />
            </motion.div>
          </div>

          {/* Right: Booking Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="lg:sticky lg:top-24"
          >
            <BookingForm 
              defaultCountry={preferences.country}
              defaultCity={preferences.city}
            />
          </motion.div>
        </div>

        {/* Social Proof - Mobile */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="lg:hidden mt-12"
        >
          <SocialProofBanner 
            language={language} 
            stats={{ providers: 500, requests: 10000, countries: 27 }}
          />
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="border-t bg-white py-8 mt-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xl font-bold text-orange-500">KEIKKA</span>
              <span className="text-muted-foreground">
                {t('serviceMvp.footer.tagline') || 'Service Marketplace'}
              </span>
            </div>
            <div className="flex gap-6 text-sm text-muted-foreground">
              <Link to="/legal/privacy" className="hover:text-foreground transition">
                {t('serviceMvp.footer.privacy') || 'Privacy'}
              </Link>
              <Link to="/legal/terms" className="hover:text-foreground transition">
                {t('serviceMvp.footer.terms') || 'Terms'}
              </Link>
            </div>
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} Keikka. {t('booking.footer.rights') || 'All rights reserved.'}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BookingPage;
